import { action, computed, makeObservable, observable } from 'mobx';

import Auth from './AuthManager';

const GOOGLE_CLASSROOM_ENDPOINTS = {
  CHECK_GOOGLE_CLASSROOM_AUTHORIZATION: '/api/checkGoogleClassroomAuthorization',
  CONNECT_GOOGLE_TO_CLASSROOM: '/api/addGoogleToClassroom',
  CONNECT_GOOGLE_TO_USER: '/api/addGoogleToUser',
  DISCONNECT_GOOGLE_FROM_CLASSROOM: '/api/removeGoogleToClassroom',
  DISCONNECT_GOOGLE_FROM_USER: '/api/removeGoogleToUser',
  FETCH_GOOGLE_CLASSROOMS: '/api/viewGoogleClassrooms',
  FETCH_GOOGLE_CLASSROOM_AUTH_URL: '/api/getGoogleClassroomAuthUrl',
  FETCH_GOOGLE_CLASSROOM_BY_ID: '/api/viewGoogleClassroom',
  FETCH_GOOGLE_CLASSROOM_ROSTER: '/api/viewGoogleClassroomRoster',
  IMPORT_GOOGLE_CLASSROOMS: '/api/importGoogleToClassrooms',
  IMPORT_GOOGLE_CLASSROOM_USERS: '/api/importGoogleToClassroomRoster'
};

export class GoogleClassroomManager {
  constructor() {
    makeObservable(this);
  }

  @observable allowGoogleClassroomIntegration = false;

  @observable googleClassroomsMap = new Map();

  @observable selectedGoogleClassroomOptionsMap = new Map();

  @observable googleClassroomRosterMap = new Map();

  @observable selectedGoogleClassroomRosterOptionsMap = new Map();

  @action clearAll = () => {
    this.clearGoogleClassroomsMap();
    this.clearSelectedGoogleClassroomOptionsMap();
    this.clearGoogleClassroomRosterMap();
    this.clearSelectedGoogleClassroomRosterOptionsMap();
  }

  @action setAllowGoogleClassroomIntegration = (toggle) => {
    this.allowGoogleClassroomIntegration = toggle;
  }

  checkGoogleClassroomAuthorization = async () => {
    try {
      const { CHECK_GOOGLE_CLASSROOM_AUTHORIZATION } = GOOGLE_CLASSROOM_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${CHECK_GOOGLE_CLASSROOM_AUTHORIZATION}`;
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      if (response.status === 'SUCCESS') {
        return !!response?.hasAuthorization;
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  }

  fetchGoogleClassroomAuthUrl = async () => {
    try {
      const { FETCH_GOOGLE_CLASSROOM_AUTH_URL } = GOOGLE_CLASSROOM_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${FETCH_GOOGLE_CLASSROOM_AUTH_URL}`;
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      if (response.status === 'SUCCESS') {
        return response?.googleClassroomAuthUrl || '';
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  }

  fetchGoogleClassrooms = async () => {
    try {
      const { FETCH_GOOGLE_CLASSROOMS } = GOOGLE_CLASSROOM_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${FETCH_GOOGLE_CLASSROOMS}`;
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      if (response.status === 'SUCCESS') {
        return response?.data || [];
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  }

  // TODO unused
  fetchGoogleClassroomById = async ({ googleClassroomId } = {}) => {
    try {
      const { FETCH_GOOGLE_CLASSROOM_BY_ID } = GOOGLE_CLASSROOM_ENDPOINTS;
      let apiUrl = `${Auth.ecms}${FETCH_GOOGLE_CLASSROOM_BY_ID}`;
      apiUrl += `?googleClassroomId=${googleClassroomId}`;
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      if (response.status === 'SUCCESS') {
        return response?.data || [];
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  }

  @action setGoogleClassroomsMap = (googleClassroomsMap) => {
    this.googleClassroomsMap = googleClassroomsMap;
  }

  @action clearGoogleClassroomsMap = () => {
    this.googleClassroomsMap = new Map();
  }

  @action setSelectedGoogleClassroomOptionsMap = (selectedGoogleClassroomOptionsMap) => {
    this.selectedGoogleClassroomOptionsMap = selectedGoogleClassroomOptionsMap;
  }

  @action addToSelectedGoogleClassroomOptionsMap = (googleClassroom) => {
    if (googleClassroom?.id) {
      this.selectedGoogleClassroomOptionsMap.set(googleClassroom.id, googleClassroom);
    }
  }

  @action removeFromSelectedGoogleClassroomOptionsMap = (googleClassroomId) => {
    if (googleClassroomId) {
      this.selectedGoogleClassroomOptionsMap.delete(googleClassroomId);
    }
  }

  @action clearSelectedGoogleClassroomOptionsMap = () => {
    this.selectedGoogleClassroomOptionsMap = new Map();
  }

  importGoogleClassrooms = async ({ googleClassroomIds = [] } = {}) => {
    try {
      const { IMPORT_GOOGLE_CLASSROOMS } = GOOGLE_CLASSROOM_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${IMPORT_GOOGLE_CLASSROOMS}`;

      const googleClassroomIdsStr = googleClassroomIds.join();

      const body = {
        googleClassroomIds: googleClassroomIdsStr
      };
      const response = await Auth.fetch(apiUrl, { body, method: 'POST' });

      if (response.status === 'SUCCESS') {
        return {
          ...response,
          success: true
        };
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  }

  connectGoogleToClassroom = async ({ classroomId, googleClassroomId } = {}) => {
    try {
      const { CONNECT_GOOGLE_TO_CLASSROOM } = GOOGLE_CLASSROOM_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${CONNECT_GOOGLE_TO_CLASSROOM}`;
      const body = {
        googleClassroomId,
        id: classroomId
      };
      const response = await Auth.fetch(apiUrl, { body, method: 'POST' });
      if (response.status === 'SUCCESS') {
        return {
          ...response,
          success: true
        };
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  }

  disconnectGoogleFromClassroom = async ({ classroomId } = {}) => {
    try {
      const { DISCONNECT_GOOGLE_FROM_CLASSROOM } = GOOGLE_CLASSROOM_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${DISCONNECT_GOOGLE_FROM_CLASSROOM}`;
      const body = {
        id: classroomId
      };
      const response = await Auth.fetch(apiUrl, { body, method: 'POST' });
      if (response.status === 'SUCCESS') {
        return {
          ...response,
          success: true
        };
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  };

  fetchGoogleClassroomRoster = async ({ googleClassroomId } = {}) => {
    try {
      const { FETCH_GOOGLE_CLASSROOM_ROSTER } = GOOGLE_CLASSROOM_ENDPOINTS;
      let apiUrl = `${Auth.ecms}${FETCH_GOOGLE_CLASSROOM_ROSTER}`;
      apiUrl += `?googleClassroomId=${googleClassroomId}`;
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      if (response.status === 'SUCCESS') {
        return response?.data || [];
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  }

  @action setGoogleClassroomRosterMap = (googleClassroomRosterMap) => {
    this.googleClassroomRosterMap = googleClassroomRosterMap;
  }

  @action clearGoogleClassroomRosterMap = () => {
    this.googleClassroomRosterMap = new Map();
  }

  @action addToSelectedGoogleClassroomRosterOptionsMap = (googleClassroomUser) => {
    if (googleClassroomUser?.id) {
      this.selectedGoogleClassroomRosterOptionsMap.set(googleClassroomUser.id, googleClassroomUser);
    }
  }

  @action removeFromSelectedGoogleClassroomRosterOptionsMap = (googleClassroomUserId) => {
    if (googleClassroomUserId) {
      this.selectedGoogleClassroomRosterOptionsMap.delete(googleClassroomUserId);
    }
  }

  @action clearSelectedGoogleClassroomRosterOptionsMap = () => {
    this.selectedGoogleClassroomRosterOptionsMap = new Map();
  }

  importGoogleClassroomRosterUsers = async ({ classroomId, googleClassroomUserIds = [] }) => {
    try {
      const { IMPORT_GOOGLE_CLASSROOM_USERS } = GOOGLE_CLASSROOM_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${IMPORT_GOOGLE_CLASSROOM_USERS}`;

      const googleClassroomUserIdsStr = googleClassroomUserIds.join();

      const body = {
        classroomId,
        googleStudentIds: googleClassroomUserIdsStr
      };
      const response = await Auth.fetch(apiUrl, { body, method: 'POST' });

      if (response.status === 'SUCCESS') {
        return {
          ...response,
          success: true
        };
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  }

  connectGoogleToUser = async ({
    // googleClassroomId,
    googleClassroomUserId,
    userId
  } = {}) => {
    try {
      const { CONNECT_GOOGLE_TO_USER } = GOOGLE_CLASSROOM_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${CONNECT_GOOGLE_TO_USER}`;
      const body = {
        // googleClassroomId,
        googleClassroomId: googleClassroomUserId,
        id: userId
      };
      const response = await Auth.fetch(apiUrl, { body, method: 'POST' });
      if (response.status === 'SUCCESS') {
        return {
          ...response,
          success: true
        };
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  }

  disconnectGoogleFromUser = async ({ userId }) => {
    try {
      const { DISCONNECT_GOOGLE_FROM_USER } = GOOGLE_CLASSROOM_ENDPOINTS;
      const apiUrl = `${Auth.ecms}${DISCONNECT_GOOGLE_FROM_USER}`;
      const body = {
        id: userId
      };
      const response = await Auth.fetch(apiUrl, { body, method: 'POST' });
      if (response.status === 'SUCCESS') {
        return {
          ...response,
          success: true
        };
      } else {
        console.error(response);
      }
    } catch (error) {
      console.error(error);
    }
  }

  @computed get allGoogleClassroomsArray() {
    return Array.from(this.googleClassroomsMap.values());
  }

  @computed get unlinkedGoogleClassroomsArray() {
    return Array.from(this.googleClassroomsMap.values()).filter((googleClassroom) => {
      return !googleClassroom?.classroomId;
    });
  }

  @computed get selectedGoogleClassroomsArray() {
    return Array.from(this.selectedGoogleClassroomOptionsMap.values());
  }

  @computed get allGoogleClassroomRosterUsersArray() {
    return Array.from(this.googleClassroomRosterMap.values());
  }

  @computed get unlinkedGoogleClassroomRosterUsersArray() {
    return Array.from(this.googleClassroomRosterMap.values()).filter((googleClassroomUser) => {
      return !googleClassroomUser?.userId;
    });
  }

  @computed get selectedGoogleClassroomsRosterUsersArray() {
    return Array.from(this.selectedGoogleClassroomRosterOptionsMap.values());
  }
}

export default new GoogleClassroomManager();
